import { useEffect, useState } from "react";
import { Bell, Play, RefreshCw, Clock, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import adminApi from "@/lib/adminApi";

const JOB_LABELS = {
  daily_summary: { label: "Daily summary", desc: "20:00 Lagos — today's sales recap to all opted-in users" },
  weekly_summary: { label: "Weekly summary", desc: "Sun 19:00 Lagos — 7-day PNG snapshot" },
  low_stock_alert: { label: "Low-stock alert", desc: "Mon-Sat 09:00 Lagos — restock nudge" },
  debtor_reminder: { label: "Debtor reminder", desc: "Mon 10:00 Lagos — debts >30 days" },
};

const STATUS_TONE = {
  sent: "text-emerald-700 bg-emerald-50",
  no_message_id: "text-amber-700 bg-amber-50",
  error: "text-rose-700 bg-rose-50",
};

export default function AdminScheduler() {
  const [jobs, setJobs] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);
  const [toast, setToast] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [j, l] = await Promise.all([
        adminApi.get("/admin/scheduler/jobs"),
        adminApi.get("/admin/notifications/log?limit=100"),
      ]);
      setJobs(j.data.jobs || []);
      setLog(l.data.items || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function runNow(job) {
    setTriggering(job);
    try {
      const r = await adminApi.post("/admin/scheduler/run-now", { job });
      setToast(r.data.ok ? `Triggered: ${job}` : `Failed: ${r.data.error}`);
      setTimeout(() => setToast(null), 3500);
      setTimeout(load, 2500);
    } catch (e) {
      setToast(`Error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTriggering(null);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-scheduler">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Bell className="w-6 h-6" /> Notifications & scheduler</h1>
          <p className="text-sm text-kudi-ink/55 mt-1">Cron jobs send proactive WhatsApp messages to opted-in users.</p>
        </div>
        <button onClick={load} className="text-sm text-kudi-ink/70 hover:text-kudi-ink flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-white" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {toast && (
        <div className="mb-4 bg-kudi-ink text-white px-4 py-2.5 rounded-lg text-sm flex items-center gap-2" data-testid="scheduler-toast">
          <AlertCircle className="w-4 h-4" /> {toast}
        </div>
      )}

      {/* Jobs */}
      <section className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="jobs-grid">
        {jobs.map((j) => {
          const meta = JOB_LABELS[j.id] || { label: j.id, desc: j.trigger };
          return (
            <div key={j.id} className="bg-white border border-border rounded-2xl p-4" data-testid={`job-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold">{meta.label}</div>
                  <div className="text-xs text-kudi-ink/55 mt-0.5">{meta.desc}</div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-kudi-ink/65 bg-accent/40 rounded-full px-2.5 py-1">
                    <Clock className="w-3 h-3" /> Next: {j.next_run ? new Date(j.next_run).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                  </div>
                </div>
                <button
                  onClick={() => runNow(j.id)}
                  disabled={triggering === j.id}
                  className="bg-kudi-green text-white text-xs font-semibold rounded-lg px-3 py-2 flex items-center gap-1.5 hover:bg-kudi-green-dark active:scale-[0.97] transition disabled:opacity-50"
                  data-testid={`run-${j.id}`}
                >
                  <Play className="w-3 h-3" /> {triggering === j.id ? "…" : "Run now"}
                </button>
              </div>
            </div>
          );
        })}
        {!loading && jobs.length === 0 && (
          <div className="col-span-2 text-sm text-kudi-ink/55 bg-white border border-border rounded-2xl p-6 text-center">
            Scheduler not initialised. Check backend logs.
          </div>
        )}
      </section>

      {/* Notification log */}
      <section>
        <h2 className="font-heading text-lg font-bold mb-3">Recent dispatches</h2>
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          {log.length === 0 ? (
            <div className="p-8 text-center text-sm text-kudi-ink/50" data-testid="log-empty">
              No notifications sent yet. Use “Run now” on any job to test, or wait for the next cron.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="log-table">
              <thead className="bg-accent/30 text-xs uppercase text-kudi-ink/60">
                <tr>
                  <th className="px-4 py-2.5 text-left">When</th>
                  <th className="px-4 py-2.5 text-left">Job</th>
                  <th className="px-4 py-2.5 text-left">User</th>
                  <th className="px-4 py-2.5 text-left">Status</th>
                  <th className="px-4 py-2.5 text-left">Detail</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`log-row-${r.id}`}>
                    <td className="px-4 py-2 text-kudi-ink/70 whitespace-nowrap">{new Date(r.at).toLocaleString("en-NG", { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="px-4 py-2 font-medium">{JOB_LABELS[r.kind]?.label || r.kind}</td>
                    <td className="px-4 py-2 text-kudi-ink/65 font-mono text-xs">{r.user_id?.slice(0, 8)}…</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "text-kudi-ink/55 bg-muted"}`}>
                        {r.status === "sent" ? <CheckCircle2 className="w-3 h-3" /> : r.status === "error" ? <XCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-kudi-ink/55 truncate max-w-[180px]" title={r.detail}>{r.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
